import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import {MutationCache, QueryCache, QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {describeError, installGlobalErrorLogging, log} from "./utilities/logger";
import {theme} from "./components/theme";
import {CssBaseline, ThemeProvider} from "@mui/material";

installGlobalErrorLogging();

// Anything a query or mutation throws is logged once here, when its retries are used up. A 4xx is the server
// refusing the request (a warning); anything else (5xx, no connection, a bug in the code) is an error.
const logFailure = (what: string, detail: Record<string, unknown>) => (error: unknown) => {
    const details = describeError(error);
    const status = typeof details.status === 'number' ? details.status : undefined;
    log[status !== undefined && status < 500 ? 'warn' : 'error'](`${what} failed`, { ...detail, ...details, error: status === undefined ? error : undefined });
};

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, query) => logFailure('query', { queryKey: JSON.stringify(query.queryKey).slice(0, 120) })(error),
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) =>
            logFailure('mutation', { mutationKey: JSON.stringify(mutation.options.mutationKey ?? null) })(error),
    }),
}); // ✅ define once


ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
                <App />
            </QueryClientProvider>
        </ThemeProvider>
    </React.StrictMode>
);